<script setup lang="ts">
/**
 * Upgrade sheet — brief §17.
 * Native: PaywallSheet behaviour (price, buy, restore).
 * Web: GetAppSheet behaviour (store links, no purchase flow).
 */
import { computed } from 'vue';
import { Capacitor } from '@capacitor/core';
import { IconRefresh, IconX } from '@tabler/icons-vue';
import { useEntitlementStore } from '@/stores/entitlement';
import { useUiStore } from '@/stores/ui';
import ComingSoonStores from '@/components/paywall/ComingSoonStores.vue';

const entitlement = useEntitlementStore();
const ui = useUiStore();

const isNative = Capacitor.isNativePlatform();

const headline = computed(() => {
  switch (ui.paywallContext) {
    case 'trace':
      return 'Tracing is a Pro feature';
    case 'fill':
      return 'Bucket fill is a Pro feature';
    case 'export':
      return 'This export option is a Pro feature';
    default:
      return 'This pattern is a Pro shape';
  }
});

function close(): void {
  ui.showPaywall = false;
}

async function buy(): Promise<void> {
  await entitlement.purchase();
  if (entitlement.isPro) close();
}

async function restore(): Promise<void> {
  await entitlement.restore();
  if (entitlement.isPro) close();
}
</script>

<template>
  <div v-if="ui.showPaywall" class="sheet-backdrop" @click.self="close">
    <div class="sheet" role="dialog" aria-modal="true">
      <h2>{{ headline }}</h2>

      <ul class="features">
        <li>Trace layer — turn your sketch into clean vector outlines</li>
        <li>Export for editing (PSD), vector (SVG/DXF), and larger wallpaper sizes</li>
        <li>All tessellation shapes and every symmetry</li>
        <li>One-time purchase. No subscription.</li>
      </ul>

      <template v-if="isNative">
        <button class="buy-btn" :disabled="entitlement.purchasePending" @click="buy">
          Unlock Escherllate Pro — £14.99
        </button>
        <button class="link-btn" :disabled="entitlement.purchasePending" @click="restore">
          <IconRefresh :size="14" stroke-width="1.75" />
          Restore purchase
        </button>
      </template>

      <template v-else>
        <p class="web-note">Sketching is free here forever. The full toolkit lives in the app.</p>
        <ComingSoonStores size="md" />
      </template>

      <button class="link-btn" @click="close">
        <IconX :size="14" stroke-width="1.75" />
        Keep sketching free
      </button>
    </div>
  </div>
</template>

<style scoped>
.sheet-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(8, 8, 16, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.sheet {
  width: min(420px, calc(100vw - 32px));
  background: var(--ui-surface);
  border: 1px solid var(--ui-border);
  border-radius: 12px;
  padding: 24px;
}

.sheet h2 {
  margin: 0 0 12px;
  font-size: 18px;
  color: var(--text-primary);
}

.features {
  margin: 0 0 20px;
  padding-left: 18px;
  color: var(--text-muted);
  font-size: 13px;
  line-height: 1.7;
}

.buy-btn {
  display: block;
  width: 100%;
  min-height: 44px;
  background: var(--accent);
  color: var(--accent-contrast);
  font-size: 15px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 8px;
}

.buy-btn:disabled {
  opacity: 0.6;
}

.web-note {
  color: var(--text-muted);
  font-size: 13px;
  margin: 0 0 12px;
}

.coming-soon-stores {
  margin-bottom: 12px;
}

.link-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 100%;
  min-height: 36px;
  background: transparent;
  color: var(--text-muted);
  border: none;
  cursor: pointer;
  font-size: 13px;
}

.link-btn:hover {
  color: var(--text-primary);
}
</style>
